import React, { useState } from 'react';
import { Box, Modal, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import CommonModalButton from '../../../../form-components/CommonModalButton';
import CommonLoading from '../../../../form-components/CommonLoading';
import CommonPagination from '../../../../form-components/CommonPagination';
import { getGroupLessonTemplates, deleteGroupLessonTemplate } from '../../../../api/lesson';

import TemplateFormModal from './TemplateFormModal';
import TemplateEditModal from './TemplateEditModal';
import LinkedScheduleModal from './LinkedScheduleModal';

import {
  ManageContainer,
  ModalHeader,
  ModalHeaderTitle,
  ManageContent,
  TemplateTable,
  TableHead,
  TableBody,
  ActionButton,
  ModalFooter,
  EmptyMessage,
} from './GroupLessonTemplateStyled';

const DeleteConfirmDialog = ({ open, onClose, onConfirm, isLoading }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>그룹수업 삭제</DialogTitle>
    <DialogContent>
      <Typography sx={{ fontSize: '14px', color: '#333' }}>이 그룹수업을 삭제하시겠습니까?</Typography>
      <Typography sx={{ fontSize: '13px', color: '#999', mt: '6px' }}>삭제된 그룹수업은 복구할 수 없습니다.</Typography>
      <Typography sx={{ fontSize: '13px', color: '#E67E22', mt: '8px' }}>연결된 수업이 있는 경우 그룹수업 연결이 해제됩니다.</Typography>
    </DialogContent>
    <DialogActions sx={{ p: '12px 24px' }}>
      <CommonModalButton variant="cancel" onClick={onClose}>취소</CommonModalButton>
      <CommonModalButton variant="delete" onClick={onConfirm} disabled={isLoading}>삭제</CommonModalButton>
    </DialogActions>
  </Dialog>
);

const LIMIT = 10;

const TemplateManageModal = ({ open, onClose, tickets = [] }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const user = useSelector(state => state.user.user);

  const [page, setPage] = useState(1);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [scheduleTemplate, setScheduleTemplate] = useState(null);

  // 템플릿 목록 조회
  const { data: templatesData, isLoading } = useQuery({
    queryKey: ['groupLessonTemplates', user?.branch?.branchId, page],
    queryFn: () => getGroupLessonTemplates(user.branch.branchId, user.token, page, LIMIT),
    enabled: !!user?.branch?.branchId && !!user?.token && open,
  });
  const templates = templatesData?.content ?? [];
  const totalElements = templatesData?.totalElements ?? 0;

  // 템플릿 삭제
  const deleteMutation = useMutation({
    mutationFn: id => deleteGroupLessonTemplate(id, user.token),
    onSuccess: () => {
      toast.success('삭제되었습니다');
      queryClient.invalidateQueries({ queryKey: ['groupLessonTemplates'] });
    },
  });

  const handleDelete = id => {
    setDeleteTarget(id);
  };

  const handleConfirmDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget);
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose} sx={{ '& :focus-visible': { outline: 'none' } }}>
        <ManageContainer>
          <ModalHeader>
            <ModalHeaderTitle>그룹수업 관리</ModalHeaderTitle>
            <CloseIcon onClick={onClose} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
          </ModalHeader>

          <ManageContent>
            <Box sx={{ backgroundColor: '#fef6f6', border: '1px solid #f5d5d5', borderRadius: '6px', padding: '10px 14px', mb: '14px' }}>
              <Typography sx={{ fontSize: '12px', color: '#c44', lineHeight: 1.6 }}>
                *자주 사용하는 수업 설정을 그룹수업으로 등록해 두면, 스케줄 등록 시 선택하여 빠르게 적용할 수 있습니다.
              </Typography>
            </Box>
            {isLoading ? (
              <CommonLoading label="불러오는 중..." minHeight={200} />
            ) : templates.length === 0 ? (
              <EmptyMessage>등록된 그룹수업이 없습니다</EmptyMessage>
            ) : (
              <>
                <TemplateTable>
                  <TableHead>
                    <tr>
                      <th style={{ width: '120px' }}>그룹수업명</th>
                      <th style={{ width: '90px' }}>연결된 수강권</th>
                      <th style={{ width: '60px' }}>시간</th>
                      <th style={{ width: '50px' }}>차감</th>
                      <th style={{ width: '50px' }}>정원</th>
                      <th style={{ width: '50px' }}>대기</th>
                      {/* <th style={{ width: '90px' }}>연결된 스케줄</th> */}
                      <th style={{ width: '100px' }}>관리</th>
                    </tr>
                  </TableHead>
                  <TableBody>
                    {templates.map(tpl => (
                      <tr key={tpl.id}>
                        <td style={{ fontWeight: 600 }}>{tpl.templateName}</td>
                        <td>{tpl.selectedTicketCount}개</td>
                        <td>{tpl.classDuration}분</td>
                        <td>{tpl.ticketDeductionCount}회</td>
                        <td>{tpl.membersMaxCount}명</td>
                        <td>{tpl.membersWaitMaxCount}명</td>
                        {/* <td>
                          <ActionButton buttonColor="edit" onClick={() => setScheduleTemplate(tpl)}>
                            보기
                          </ActionButton>
                        </td> */}
                        <td>
                          <ActionButton buttonColor="edit" onClick={() => setEditTemplate(tpl)}>
                            수정
                          </ActionButton>
                          <ActionButton
                            buttonColor="delete"
                            onClick={() => handleDelete(tpl.id)}
                            disabled={deleteMutation.isPending}
                            sx={{ ml: '4px' }}>
                            {deleteMutation.isPending ? '삭제 중...' : '삭제'}
                          </ActionButton>
                        </td>
                      </tr>
                    ))}
                  </TableBody>
                </TemplateTable>
                <CommonPagination
                  totalElements={totalElements}
                  limit={LIMIT}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                />
              </>
            )}
          </ManageContent>

          <ModalFooter>
            <CommonModalButton variant="cancel" onClick={onClose}>
              닫기
            </CommonModalButton>
            <CommonModalButton variant="submit" onClick={() => setCreateModalOpen(true)}>
              + 그룹수업 만들기
            </CommonModalButton>
          </ModalFooter>
        </ManageContainer>
      </Modal>

      {/* 템플릿 생성 모달 */}
      <TemplateFormModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        tickets={tickets}
      />

      {/* 그룹수업 수정 모달 */}
      <TemplateEditModal
        open={!!editTemplate}
        onClose={() => setEditTemplate(null)}
        template={editTemplate}
        tickets={tickets}
      />

      {/* 연결된 스케줄 모달 */}
      <LinkedScheduleModal
        open={!!scheduleTemplate}
        onClose={() => setScheduleTemplate(null)}
        template={scheduleTemplate}
      />

      {/* 삭제 확인 */}
      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </>
  );
};

export default TemplateManageModal;
